import React from 'react';
import { Bell, Menu, X, LogOut, Home, Users, BarChart3, Shield, HelpCircle, Search, Share2, Globe, History, UserCircle } from 'lucide-react';
import NotificationBell from './NotificationBell';

interface LayoutProps {
  children: React.ReactNode;
  activeTab: string;
  onTabChange: (tab: string) => void;
  currentUserId?: string | null;
  isAdmin?: boolean;
  onSearchOpen?: () => void;
  onHelpOpen?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, activeTab, onTabChange, currentUserId, isAdmin = false, onSearchOpen, onHelpOpen }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const navItems = [
    { id: 'feed', label: 'Feed', icon: Home },
    { id: 'tree', label: 'Family Tree', icon: Users },
    { id: 'circles', label: 'Circles', icon: Share2 },
    { id: 'map', label: 'Map', icon: Globe },
    { id: 'timeline', label: 'Chronicles', icon: History },
    { id: 'directory', label: 'Directory', icon: Search },
    { id: 'insights', label: 'Insights', icon: BarChart3 },
    { id: 'profile', label: 'Profile', icon: UserCircle },
  ];

  if (isAdmin) {
    navItems.push({ id: 'admin', label: 'Admin', icon: Shield });
  }

  const isFullScreen = activeTab === 'tree' || activeTab === 'map' || activeTab === 'circles';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row relative font-sans text-slate-900">
      
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-gray-200 h-screen sticky top-0 z-20">
        <div className="p-6 border-b border-gray-100 flex items-center space-x-2">
          <h1 className="text-3xl text-gray-900 tracking-tighter font-black">Famitry</h1>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto no-scrollbar">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-indigo-50 text-indigo-600 font-semibold shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Search, Help, and Theme Switcher */}
        <div className="px-4 py-2 border-t border-gray-100 space-y-2">
          <button
            onClick={onSearchOpen}
            className="w-full flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700 text-sm font-medium"
          >
            <Search className="w-4 h-4" />
            <span>Search...</span>
          </button>
          <button
            onClick={onHelpOpen}
            className="w-full flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-blue-700 text-sm font-medium"
          >
            <HelpCircle className="w-4 h-4" />
            <span>Tutorials & Help</span>
          </button>
        </div>
        
        {/* Optional: Sidebar Footer Status */}
        <div className="p-4 border-t border-gray-100 mt-auto">
           <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-4 rounded-2xl text-white shadow-lg">
             <p className="text-xs font-bold opacity-80 uppercase mb-1">Family Status</p>
             <p className="text-sm font-bold">3 Quests Active</p>
           </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header
        className={`z-50 md:hidden ${
          isFullScreen
            ? "absolute top-0 left-0 right-0 border-b-0 bg-white/80 backdrop-blur-md"
            : "sticky top-0 border-b border-gray-200 bg-white"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <span className="text-xl font-black tracking-tight text-gray-900">
              Famitry
            </span>
          </div>

          <div className="flex gap-2 items-center">
             {/* Search Button */}
             <button
               onClick={onSearchOpen}
               className="p-2 text-gray-600 transition-colors hover:text-gray-900"
             >
               <Search className="w-5 h-5" />
             </button>

             {/* Help Button */}
             <button
               onClick={onHelpOpen}
               className="p-2 text-gray-600 transition-colors hover:text-gray-900"
               title="Video Tutorials & Help"
             >
               <HelpCircle className="w-5 h-5" />
             </button>

             {/* Notification Bell Component */}
             <NotificationBell currentUserId={currentUserId || null} />

             {/* Menu Toggle Button */}
             <button
               onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
               className="p-2 text-gray-600 transition-colors hover:text-gray-900"
               aria-label="Toggle menu"
             >
               {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
             </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu (Overlay) */}
        {isMobileMenuOpen && (
          <div className="bg-white border-t border-gray-100 absolute w-full shadow-xl z-50 animate-slide-down px-4 py-4">
            <div className="grid grid-cols-2 gap-3">
               {navItems.map((item) => (
                 <button
                   key={item.id}
                   onClick={() => {
                     onTabChange(item.id);
                     setIsMobileMenuOpen(false);
                   }}
                   className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                     activeTab === item.id 
                       ? 'bg-indigo-50 border-indigo-200 text-indigo-600' 
                       : 'bg-slate-50 border-slate-100 text-slate-500'
                   }`}
                 >
                   <item.icon className="w-6 h-6" />
                   <span className="text-xs font-bold">{item.label}</span>
                 </button>
               ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className={`flex-1 ${isFullScreen ? 'fixed inset-0 z-0 top-0 md:top-0 bg-slate-50 overflow-hidden w-screen h-screen md:h-screen' : 'overflow-y-auto p-4 md:p-8 relative h-[calc(100vh-120px)] md:h-screen'}`}>
        <div className={`mx-auto ${isFullScreen ? 'h-full w-full overflow-hidden' : 'max-w-7xl w-full'}`}>
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav (Sticky) */}
      <nav className={`md:hidden fixed bottom-0 w-full ${isFullScreen ? 'bg-white/90 backdrop-blur-md border-t-0' : 'bg-white border-t border-gray-200'} flex justify-start p-2 z-50 safe-area-bottom overflow-x-auto no-scrollbar gap-1`}>
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onTabChange(item.id)}
            className={`flex flex-col items-center p-2 rounded-lg min-w-[64px] shrink-0 transition-colors ${
              activeTab === item.id ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400'
            }`}
          >
            <item.icon className="w-6 h-6" />
            <span className="text-[10px] mt-1 font-medium">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default Layout;